import { useState, type FormEvent } from "react";
import { createShare, shareErrorMessage } from "../shares/api";
import { SharedWithPanel } from "../shares/SharedWithPanel";

// Share-with-contacts UI for an Excalidraw scene.  Recipient resolution
// (handle / email / uuid) + revoke + permission changes all reuse the
// existing user-targeted share endpoints; this just adds the create form and
// composes SharedWithPanel for the "shared with" list.

export function SceneShareModal({
  sceneId,
  accessToken,
  onClose,
}: {
  sceneId: string;
  accessToken: string;
  onClose: () => void;
}) {
  const [recipient, setRecipient] = useState("");
  const [permission, setPermission] = useState<"read" | "edit">("edit");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = recipient.trim();
    if (!r || busy) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await createShare(accessToken, {
        recipient: r,
        scope: "excalidraw",
        scopeId: sceneId,
        permissions: permission,
      });
      setRecipient("");
      setRev((v) => v + 1);
      setInfo(
        res.deliveredTo === "email"
          ? "Invite sent by email — they'll get access when they sign in."
          : "Shared. They'll see it once they accept.",
      );
    } catch (err) {
      setError(
        shareErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <form onSubmit={submit} className="flex flex-col gap-2">
        <label className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
          Share with a contact (handle or email)
        </label>
        <div className="flex gap-2">
          <input
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="@handle or email@example.com"
            className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
          <select
            value={permission}
            onChange={(e) => setPermission(e.target.value as "read" | "edit")}
            className="rounded border border-slate-300 bg-white px-1 py-1 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            aria-label="Permission level"
          >
            <option value="edit">can edit</option>
            <option value="read">can view</option>
          </select>
          <button
            type="submit"
            disabled={busy || !recipient.trim()}
            className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            Share
          </button>
        </div>
        {error && <p className="text-[11px] text-rose-600 dark:text-rose-400">{error}</p>}
        {info && <p className="text-[11px] text-emerald-700 dark:text-emerald-300">{info}</p>}
        <p className="text-[11px] text-slate-400 dark:text-slate-500">
          Editors can draw on this page live with you; viewers see changes in
          real time but can't edit.
        </p>
      </form>

      <SharedWithPanel
        scope="excalidraw"
        scopeId={sceneId}
        accessToken={accessToken}
        rev={rev}
        onChanged={() => setRev((v) => v + 1)}
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Done
        </button>
      </div>
    </div>
  );
}
